// Zentraler API-Client: einheitliche Requests, Session-Cookie, RFC-7807-Fehler.
import type { components } from './schema';

type Problem = components['schemas']['Problem'];
export type FieldError = components['schemas']['FieldError'];

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: FieldError[];
  readonly requestId?: string;

  constructor(status: number, code: string, message: string, fieldErrors: FieldError[] = [], requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.requestId = requestId;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export type QueryValue = string | number | boolean | string[] | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

const baseUrl = `${(import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')}/api/v1`;

// apiProblemEvent: fehlgeschlagene Aktion (nicht GET, nicht 401) – die KI-Hilfe bietet dazu von sich aus Hilfe an.
export const apiProblemEvent = 'em:api-problem';
export interface ApiProblem {
  status: number;
  code: string;
  message: string;
}

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    for (const v of Array.isArray(value) ? value : [value]) params.append(key, String(v));
  }
  const qs = params.toString();
  return `${baseUrl}${path}${qs ? `?${qs}` : ''}`;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;
  const headers: Record<string, string> = { Accept: 'application/json' };
  // Dateien (z. B. Bilder) gehen unverändert als Body, alles andere als JSON.
  const file = typeof Blob !== 'undefined' && body instanceof Blob ? body : null;
  if (body !== undefined) headers['Content-Type'] = file ? file.type || 'application/octet-stream' : 'application/json';

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: file ?? (body !== undefined ? JSON.stringify(body) : undefined),
      credentials: 'include',
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'network_error', 'Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Verbindung.');
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const data = text ? parseJson(text) : undefined;

  if (!response.ok) {
    const problem = (data ?? {}) as Partial<Problem>;
    const error = new ApiError(
      response.status,
      problem.code ?? 'http_error',
      problem.detail ?? problem.title ?? `Unerwarteter Fehler (HTTP ${response.status}).`,
      problem.errors ?? [],
      problem.request_id ?? response.headers.get('X-Request-ID') ?? undefined,
    );
    if (method !== 'GET' && response.status !== 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent<ApiProblem>(apiProblemEvent, { detail: { status: error.status, code: error.code, message: error.message } }));
    }
    throw error;
  }
  return data as T;
}

// Benutzerfreundliche Meldung für beliebige Fehler.
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 0) return error.message;
    if (error.status === 403) return 'Für diese Aktion fehlt Ihnen die Berechtigung.';
    if (error.status >= 500) return 'Auf dem Server ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
    return error.message;
  }
  return 'Ein unerwarteter Fehler ist aufgetreten.';
}
